import { useState, useCallback, useRef, useMemo } from 'react';
import Map, { NavigationControl, MapRef } from 'react-map-gl';
import { useTheme } from 'next-themes';
import { MapLocation } from '@/types/location';
import { LocationOverride } from '@/hooks/useLocationOverrides';
import { MapboxMarkerLayer } from './MapboxMarkerLayer';
import { MapboxRegionController } from './MapboxRegionController';
import { MapboxFocusController } from './MapboxFocusController';
import { MapboxNetworkLines } from './MapboxNetworkLines';
import { loadMapboxIcons } from '@/utils/mapboxIcons';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

interface MapboxMapProps {
  activeRegion: string;
  filteredLocations: MapLocation[];
  selectedLocations: Set<number>;
  onLocationSelect: (id: number) => void;
  attentionScreenCounts: Map<number, number>;
  onAttentionScreenCountChange: (locationId: number, screenCount: number) => void;
  activeMediaKind: string;
  onLocationFocus: (location: MapLocation) => void;
  focusedLocation: MapLocation | null;
  isMobile: boolean;
  overrides?: Map<number, LocationOverride>;
}

export const MapboxMap = ({
  activeRegion,
  filteredLocations,
  selectedLocations,
  onLocationSelect,
  attentionScreenCounts,
  onAttentionScreenCountChange,
  activeMediaKind,
  onLocationFocus,
  focusedLocation,
  isMobile,
  overrides,
}: MapboxMapProps) => {
  const { resolvedTheme } = useTheme();
  const mapRef = useRef<MapRef>(null);

  const [viewState, setViewState] = useState({
    longitude: 45.0,
    latitude: 24.0,
    zoom: isMobile ? 4 : 5,
    pitch: 45,
    bearing: 0,
  });

  const mapStyle = useMemo(() => {
    return resolvedTheme === 'dark'
      ? 'mapbox://styles/mapbox/dark-v11'
      : 'mapbox://styles/mapbox/light-v11';
  }, [resolvedTheme]);

  const handleClick = useCallback(
    (event: mapboxgl.MapLayerMouseEvent) => {
      const features = event.features;
      if (features && features.length > 0) {
        const feature = features[0];
        const locationId = feature.properties?.locationId;
        if (locationId) {
          const location = filteredLocations.find((l) => l.id === Number(locationId));
          if (location) {
            onLocationFocus(location);
          }
        }
      }
    },
    [filteredLocations, onLocationFocus]
  );

  const setupTerrainAndBuildings = useCallback((map: mapboxgl.Map) => {
    // Add 3D terrain
    if (!map.getSource('mapbox-dem')) {
      map.addSource('mapbox-dem', {
        type: 'raster-dem',
        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
        tileSize: 512,
        maxzoom: 14,
      });
    }
    map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });

    // Add 3D buildings layer
    if (!map.getLayer('3d-buildings')) {
      const layers = map.getStyle().layers;
      const labelLayerId = layers?.find(
        (layer) => layer.type === 'symbol' && layer.layout?.['text-field']
      )?.id;

      map.addLayer(
        {
          id: '3d-buildings',
          source: 'composite',
          'source-layer': 'building',
          filter: ['==', 'extrude', 'true'],
          type: 'fill-extrusion',
          minzoom: 12,
          paint: {
            'fill-extrusion-color': resolvedTheme === 'dark' ? '#1a1a2e' : '#aaa',
            'fill-extrusion-height': ['get', 'height'],
            'fill-extrusion-base': ['get', 'min_height'],
            'fill-extrusion-opacity': 0.6,
          },
        },
        labelLayerId
      );
    }
  }, [resolvedTheme]);

  const handleMapLoad = useCallback(async () => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    setupTerrainAndBuildings(map);
    await loadMapboxIcons(map);
  }, [setupTerrainAndBuildings]);

  const handleStyleData = useCallback(async () => {
    const map = mapRef.current?.getMap();
    if (!map || !map.isStyleLoaded()) return;
    setupTerrainAndBuildings(map);
    await loadMapboxIcons(map);
  }, [setupTerrainAndBuildings]);

  const handleMouseEnter = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (map) map.getCanvas().style.cursor = 'pointer';
  }, []);

  const handleMouseLeave = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (map) map.getCanvas().style.cursor = '';
  }, []);

  return (
    <Map
      ref={mapRef}
      {...viewState}
      onMove={(evt) => setViewState(evt.viewState)}
      mapboxAccessToken={MAPBOX_TOKEN}
      mapStyle={mapStyle}
      style={{ width: '100%', height: '100%' }}
      interactiveLayerIds={['location-markers-icons']}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onLoad={handleMapLoad}
      onData={handleStyleData}
      attributionControl={false}
      reuseMaps
    >
      <NavigationControl position="bottom-left" showCompass visualizePitch />

      <MapboxNetworkLines
        filteredLocations={filteredLocations}
        selectedLocations={selectedLocations}
      />

      <MapboxMarkerLayer
        filteredLocations={filteredLocations}
        selectedLocations={selectedLocations}
        overrides={overrides}
      />

      <MapboxRegionController
        activeRegion={activeRegion}
        mapRef={mapRef}
      />

      <MapboxFocusController
        focusedLocation={focusedLocation}
        isMobile={isMobile}
        mapRef={mapRef}
      />
    </Map>
  );
};
